import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiPlus, FiSearch, FiEdit2, FiToggleLeft, FiToggleRight, FiX, FiLayers, FiClock, FiGitBranch,
  FiTrash2, FiCopy, FiSettings, FiBriefcase, FiAlertTriangle, FiRepeat,
} from 'react-icons/fi';
import { workflowsAPI, companiesAPI } from '../../services/api';
import { companyScopeLabel } from '../../components/CompanyScopeSelect/CompanyScopeSelect';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/dateFormat';
import { localizeSeeded } from './flowUtils';
import ConfirmDialog from '../../components/ConfirmDialog';
import PageHeader from '../../components/PageHeader/PageHeader';
import SearchableSelect from '../../components/SearchableSelect/SearchableSelect';
import './FlowManagement.scss';

function FlowManagement() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmWorkflow, setConfirmWorkflow] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    companiesAPI.list().then(r => setCompanies(r.data.companies || [])).catch(() => {});
  }, []);

  const loadWorkflows = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await workflowsAPI.list({ includeInactive: 1 });
      setWorkflows(data.workflows || []);
    } catch (err) {
      toast.error(t('flows.messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadWorkflows(); }, [loadWorkflows]);

  // ── Inactive users still named in workflow templates ──
  // usageKey: one selectable row = one place the user is referenced.
  const usageKey = (userId, u) => `${userId}|${u.workflowId}|${u.stepOrder}|${u.role}`;
  const [inactiveAssignees, setInactiveAssignees] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [showPassiveModal, setShowPassiveModal] = useState(false);
  const [selectedUsages, setSelectedUsages] = useState(new Set());
  const [replacementByUser, setReplacementByUser] = useState({});
  const [replaceBusy, setReplaceBusy] = useState(false);

  const loadInactiveAssignees = useCallback(async () => {
    try {
      const { data } = await workflowsAPI.inactiveAssignees();
      setInactiveAssignees(data.inactiveAssignees || []);
      setActiveUsers(data.activeUsers || []);
      // Fresh scan → everything selected by default; stale picks dropped.
      setSelectedUsages(new Set(
        (data.inactiveAssignees || []).flatMap((entry) =>
          entry.usages.map((u) => usageKey(entry.user._id, u)))
      ));
    } catch {
      setInactiveAssignees([]);
    }
  }, []);

  useEffect(() => { loadInactiveAssignees(); }, [loadInactiveAssignees]);

  const activeUserOptions = useMemo(() => activeUsers.map((u) => ({
    value: u._id,
    label: `${u.firstName} ${u.lastName}`,
    sublabel: u.email,
  })), [activeUsers]);

  const toggleUsage = (key) => {
    setSelectedUsages((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleUserAll = (entry) => {
    const keys = entry.usages.map((u) => usageKey(entry.user._id, u));
    const allOn = keys.every((k) => selectedUsages.has(k));
    setSelectedUsages((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const handleReplaceSelected = async () => {
    const replacements = [];
    for (const entry of inactiveAssignees) {
      const userId = entry.user._id;
      const usages = entry.usages.filter((u) => selectedUsages.has(usageKey(userId, u)));
      if (usages.length === 0) continue;
      if (!replacementByUser[userId]) {
        toast.error(t('flows.passive.pickReplacement', {
          name: `${entry.user.firstName} ${entry.user.lastName}`,
        }));
        return;
      }
      replacements.push({
        fromUserId: userId,
        toUserId: replacementByUser[userId],
        usages: usages.map((u) => ({ workflowId: u.workflowId, stepOrder: u.stepOrder, role: u.role })),
      });
    }
    if (replacements.length === 0) {
      toast.error(t('flows.passive.nothingSelected'));
      return;
    }
    setReplaceBusy(true);
    try {
      const { data } = await workflowsAPI.replaceAssignee({ replacements });
      toast.success(t('flows.passive.replaced', { count: data.totalReplaced }));
      setReplacementByUser({});
      await loadInactiveAssignees();
      loadWorkflows(true);
    } catch (err) {
      toast.error(err.response?.data?.error || t('flows.passive.replaceFailed'));
    } finally {
      setReplaceBusy(false);
    }
  };

  const stats = useMemo(() => ({
    all: workflows.length,
    active: workflows.filter((w) => w.isActive !== false).length,
    inactive: workflows.filter((w) => w.isActive === false).length,
  }), [workflows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workflows.filter((w) => {
      const isActive = w.isActive !== false;
      if (statusFilter === 'active' && !isActive) return false;
      if (statusFilter === 'inactive' && isActive) return false;
      if (!q) return true;
      return (
        localizeSeeded(w.name, i18n.language).toLowerCase().includes(q) ||
        localizeSeeded(w.description, i18n.language).toLowerCase().includes(q)
      );
    });
  }, [workflows, search, statusFilter]);

  const handleNew = () => navigate('/flow-management/new');
  const handleEdit = (wf) => navigate(`/flow-management/${wf._id}/edit`);

  const handleDeactivate = (wf, e) => {
    e?.stopPropagation();
    setConfirmWorkflow(wf);
  };

  const handleConfirmDeactivate = async () => {
    if (!confirmWorkflow) return;
    setConfirmBusy(true);
    try {
      await workflowsAPI.update(confirmWorkflow._id, { isActive: false });
      toast.success(t('flows.messages.workflowDeactivated'));
      setConfirmWorkflow(null);
      loadWorkflows(true);
    } catch (err) {
      toast.error(err.response?.data?.message || t('flows.messages.workflowDeleteFailed'));
      setConfirmWorkflow(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleActivate = async (wf, e) => {
    e?.stopPropagation();
    try {
      await workflowsAPI.update(wf._id, { isActive: true });
      toast.success(t('flows.messages.workflowActivated', 'Workflow activated'));
      loadWorkflows(true);
    } catch (err) {
      toast.error(err.response?.data?.message || t('flows.messages.workflowDeleteFailed'));
    }
  };

  // ── Delete (custom workflows only — the standard one never shows the action) ──
  const [deleteWorkflow, setDeleteWorkflow] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const handleConfirmDelete = async () => {
    if (!deleteWorkflow) return;
    setDeleteBusy(true);
    try {
      await workflowsAPI.delete(deleteWorkflow._id);
      toast.success(t('flows.messages.workflowDeleted'));
      setDeleteWorkflow(null);
      loadWorkflows(true);
    } catch (err) {
      // The backend explains WHY it refused (assigned to a contract type,
      // live runs, pending resends) — surface that reason as-is.
      toast.error(err.response?.data?.error || t('flows.messages.workflowDeleteFailed'));
      setDeleteWorkflow(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  // ── Copy ──
  const [copying, setCopying] = useState(null);
  const handleCopy = async (wf, e) => {
    e?.stopPropagation();
    if (copying) return;
    setCopying(wf._id);
    try {
      await workflowsAPI.duplicate(wf._id);
      toast.success(t('flows.messages.workflowCopied'));
      loadWorkflows(true);
    } catch (err) {
      toast.error(err.response?.data?.error || t('flows.messages.workflowCopyFailed'));
    } finally {
      setCopying(null);
    }
  };

  const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() || '').join('') || 'W';
  };

  const filterTabs = [
    { key: 'all', label: t('common.all', 'All'), count: stats.all },
    { key: 'active', label: t('common.active'), count: stats.active },
    { key: 'inactive', label: t('common.inactive'), count: stats.inactive },
  ];

  return (
    <div className="flow-list-page">
      {/* Standard settings-area hero — the same "Contract Settings" module
          header the /admin pages carry, so this standalone page matches. */}
      <PageHeader
        label={t('nav.businessConfig', 'Contract Settings')}
        labelIcon={<FiSettings />}
        title={t('nav.flowManagement', 'Workflows')}
        description={t('flows.subtitle')}
        actions={(
          <button className="btn btn-primary flow-list-page__new-btn" onClick={handleNew}>
            <FiPlus /> {t('flows.createWorkflow')}
          </button>
        )}
      />

      <div className="flow-list-page__toolbar">
        <div className="flow-list-page__search">
          <FiSearch />
          <input
            type="text"
            placeholder={t('flows.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="flow-list-page__search-clear"
              onClick={() => setSearch('')}
              aria-label={t('common.clear', 'Clear')}
            >
              <FiX />
            </button>
          )}
        </div>

        <div className="flow-list-page__tabs" role="tablist">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={statusFilter === tab.key}
              className={`flow-list-page__tab ${statusFilter === tab.key ? 'is-active' : ''}`}
              onClick={() => setStatusFilter(tab.key)}
            >
              {tab.label}
              <span className="flow-list-page__tab-count">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Templates still naming users who are now inactive — opens the
            bulk-replace panel. Hidden entirely while everything is clean. */}
        {inactiveAssignees.length > 0 && (
          <button
            type="button"
            className="flow-list-page__passive-chip"
            onClick={() => setShowPassiveModal(true)}
          >
            <FiAlertTriangle />
            {t('flows.passive.chip', { count: inactiveAssignees.length })}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flow-list-page__grid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flow-card flow-card--skeleton" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flow-list-page__empty">
          <div className="flow-list-page__empty-icon">
            <FiGitBranch size={28} />
          </div>
          <h3>{search || statusFilter !== 'all' ? t('flows.noMatches') : t('flows.noWorkflows')}</h3>
          <p>{search || statusFilter !== 'all' ? t('flows.tryAdjusting') : t('flows.noWorkflowsDesc')}</p>
          {!search && statusFilter === 'all' && (
            <button className="btn btn-primary" onClick={handleNew}>
              <FiPlus /> {t('flows.createFirstWorkflow')}
            </button>
          )}
        </div>
      ) : (
        <div className="flow-list-page__grid">
          {filtered.map((wf, idx) => {
            const isActive = wf.isActive !== false;
            const stepCount = wf.steps?.length ?? 0;
            return (
              <button
                key={wf._id}
                type="button"
                className={`flow-card ${isActive ? '' : 'flow-card--inactive'}`}
                style={{ animationDelay: `${Math.min(idx, 12) * 30}ms` }}
                onClick={() => handleEdit(wf)}
              >
                <div className="flow-card__top">
                  <div className="flow-card__avatar" aria-hidden="true">
                    {getInitials(localizeSeeded(wf.name, i18n.language))}
                  </div>
                  {wf.isStandard && (
                    <span className="flow-card__standard">
                      {t('flows.standardBadge', 'Standart')}
                    </span>
                  )}
                  <span className={`flow-card__status ${isActive ? 'is-active' : 'is-inactive'}`}>
                    <span className="flow-card__status-dot" />
                    {isActive ? t('common.active') : t('common.inactive')}
                  </span>
                </div>

                <div className="flow-card__body">
                  <h3 className="flow-card__name">{localizeSeeded(wf.name, i18n.language)}</h3>
                  <p className="flow-card__description">
                    {wf.description
                      ? localizeSeeded(wf.description, i18n.language)
                      : t('flows.noDescription', 'No description provided.')}
                  </p>
                </div>

                <div className="flow-card__meta">
                  <span className="flow-card__meta-item">
                    <FiLayers />
                    {t('flows.stepCount', { count: stepCount })}
                  </span>
                  <span className="flow-card__meta-item">
                    <FiBriefcase />
                    {companyScopeLabel(wf.companyIds, companies, t)}
                  </span>
                  <span className="flow-card__meta-item">
                    <FiClock />
                    {formatDate(wf.updatedAt || wf.createdAt)}
                  </span>
                </div>

                <div className="flow-card__actions">
                  <span
                    role="button"
                    tabIndex={0}
                    className="flow-card__action"
                    title={t('common.edit')}
                    onClick={(e) => { e.stopPropagation(); handleEdit(wf); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleEdit(wf); } }}
                  >
                    <FiEdit2 />
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    className={`flow-card__action ${copying === wf._id ? 'is-busy' : ''}`}
                    title={t('flows.copyWorkflow')}
                    onClick={(e) => handleCopy(wf, e)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCopy(wf, e); }}
                  >
                    <FiCopy />
                  </span>
                  {isActive && !wf.isStandard && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="flow-card__action flow-card__action--danger"
                      title={t('common.deactivate')}
                      onClick={(e) => handleDeactivate(wf, e)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleDeactivate(wf, e); }}
                    >
                      <FiToggleLeft />
                    </span>
                  )}
                  {!isActive && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="flow-card__action"
                      title={t('common.activate', 'Aktifleştir')}
                      onClick={(e) => handleActivate(wf, e)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleActivate(wf, e); }}
                    >
                      <FiToggleRight />
                    </span>
                  )}
                  {/* The standard workflow can never be deleted — the action
                      simply doesn't exist on its card. */}
                  {!wf.isStandard && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="flow-card__action flow-card__action--danger"
                      title={t('common.delete')}
                      onClick={(e) => { e.stopPropagation(); setDeleteWorkflow(wf); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setDeleteWorkflow(wf); } }}
                    >
                      <FiTrash2 />
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ═══ Passive users in templates — bulk replace ═══ */}
      {showPassiveModal && (
        <div className="flow-passive__overlay" onClick={() => !replaceBusy && setShowPassiveModal(false)}>
          <div className="flow-passive" onClick={(e) => e.stopPropagation()}>
            <div className="flow-passive__header">
              <div className="flow-passive__header-icon"><FiAlertTriangle /></div>
              <div>
                <h2>{t('flows.passive.title')}</h2>
                <p>{t('flows.passive.subtitle')}</p>
              </div>
              <button
                type="button"
                className="flow-passive__close"
                onClick={() => !replaceBusy && setShowPassiveModal(false)}
              >
                <FiX />
              </button>
            </div>

            <div className="flow-passive__body">
              {inactiveAssignees.length === 0 ? (
                <div className="flow-passive__clean">
                  <FiRepeat />
                  <p>{t('flows.passive.allClean')}</p>
                </div>
              ) : inactiveAssignees.map((entry) => {
                const userId = entry.user._id;
                const keys = entry.usages.map((u) => usageKey(userId, u));
                const allOn = keys.every((k) => selectedUsages.has(k));
                const selCount = keys.filter((k) => selectedUsages.has(k)).length;
                return (
                  <div key={userId} className="flow-passive__user">
                    <div className="flow-passive__user-head">
                      <span className="flow-passive__avatar">
                        {`${entry.user.firstName?.[0] || ''}${entry.user.lastName?.[0] || ''}`.toUpperCase() || '?'}
                      </span>
                      <div className="flow-passive__user-info">
                        <strong>
                          {entry.user.firstName} {entry.user.lastName}
                          <span className="flow-passive__badge">{t('common.inactive')}</span>
                        </strong>
                        <span>{entry.user.email}</span>
                      </div>
                      <div className="flow-passive__replacement">
                        <label>{t('flows.passive.replacement')}</label>
                        <SearchableSelect
                          value={replacementByUser[userId] || ''}
                          onChange={(v) => setReplacementByUser((prev) => ({ ...prev, [userId]: v }))}
                          options={activeUserOptions.filter((o) => o.value !== userId)}
                          placeholder={t('flows.passive.replacementPlaceholder')}
                        />
                      </div>
                    </div>

                    <table className="flow-passive__table">
                      <thead>
                        <tr>
                          <th className="flow-passive__col-check">
                            <input
                              type="checkbox"
                              checked={allOn}
                              onChange={() => toggleUserAll(entry)}
                              title={t('flows.passive.selectAll')}
                            />
                          </th>
                          <th>{t('flows.passive.colWorkflow')}</th>
                          <th>{t('flows.passive.colCompany')}</th>
                          <th>{t('flows.passive.colStep')}</th>
                          <th>{t('flows.passive.colRole')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entry.usages.map((u) => {
                          const key = usageKey(userId, u);
                          return (
                            <tr
                              key={key}
                              className={selectedUsages.has(key) ? 'is-selected' : ''}
                              onClick={() => toggleUsage(key)}
                            >
                              <td className="flow-passive__col-check" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedUsages.has(key)}
                                  onChange={() => toggleUsage(key)}
                                />
                              </td>
                              <td>
                                <strong>{localizeSeeded(u.workflowName, i18n.language)}</strong>
                                {u.workflowActive === false && (
                                  <span className="flow-passive__wf-inactive">{t('common.inactive')}</span>
                                )}
                              </td>
                              <td>
                                {u.companies?.length
                                  ? u.companies.map((c) => c.name).join(', ')
                                  : t('companyScope.allCompanies')}
                              </td>
                              <td>{u.stepOrder}. {localizeSeeded(u.stepName, i18n.language)}</td>
                              <td>
                                <span className={`flow-passive__role flow-passive__role--${u.role}`}>
                                  {t(`flows.passive.roles.${u.role}`)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="flow-passive__user-foot">
                      {t('flows.passive.selectedOfTotal', { selected: selCount, total: keys.length })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flow-passive__actions">
              <button
                type="button"
                className="btn btn-outline"
                disabled={replaceBusy}
                onClick={() => setShowPassiveModal(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={replaceBusy || inactiveAssignees.length === 0 || selectedUsages.size === 0}
                onClick={handleReplaceSelected}
              >
                <FiRepeat /> {replaceBusy ? t('common.processing') : t('flows.passive.replaceSelected')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmWorkflow}
        variant="warning"
        icon={FiToggleLeft}
        title={t('flows.deactivateTitle', 'Deactivate workflow')}
        message={confirmWorkflow ? t('flows.confirmDeactivate', { name: confirmWorkflow.name }) : ''}
        confirmLabel={t('common.deactivate')}
        loading={confirmBusy}
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setConfirmWorkflow(null)}
      />

      <ConfirmDialog
        open={!!deleteWorkflow}
        variant="danger"
        icon={FiTrash2}
        title={deleteWorkflow ? localizeSeeded(deleteWorkflow.name, i18n.language) : ''}
        message={t('flows.confirmDelete')}
        confirmLabel={t('common.delete')}
        loading={deleteBusy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteWorkflow(null)}
      />
    </div>
  );
}

export default FlowManagement;
